/**
 * Central query-key factory.
 *
 * Every key starts with its domain so a mutation can invalidate a whole area
 * with one prefix (`queryKeys.blogs.all`) instead of listing each variant.
 */
export const queryKeys = {
  /**
   * Keyed by workspace, because switching workspaces switches plan.
   *
   * `WorkspaceProvider.switchTo` clears the whole cache anyway, so this is
   * belt and braces — but a plan shown for the wrong workspace is the kind of
   * wrong that gets acted on.
   */
  billing: {
    all: ['billing'] as const,
    plans: ['billing', 'plans'] as const,
    publicPlans: ['billing', 'public-plans'] as const,
    overview: (workspaceId: string) =>
      ['billing', 'overview', workspaceId] as const,
    transactions: (workspaceId: string) =>
      ['billing', 'transactions', workspaceId] as const,
  },
  usage: {
    all: ['usage'] as const,
    summary: (workspaceId: string) =>
      ['usage', 'summary', workspaceId] as const,
  },
  blogs: {
    all: ['blogs'] as const,
    list: (params: object) => ['blogs', 'list', params] as const,
    detail: (id: string) => ['blogs', 'detail', id] as const,
    stats: (workspaceId: string) => ['blogs', 'stats', workspaceId] as const,
  },
  posts: {
    all: ['posts'] as const,
    list: (params: object) => ['posts', 'list', params] as const,
    detail: (id: string) => ['posts', 'detail', id] as const,
  },
  libraries: {
    all: ['libraries'] as const,
    list: (params: object) => ['libraries', 'list', params] as const,
    detail: (id: string) => ['libraries', 'detail', id] as const,
  },
  librarySchedules: {
    all: ['library-schedules'] as const,
    list: (libraryId: string) => ['library-schedules', libraryId] as const,
  },
  queue: {
    all: ['queue'] as const,
    upcoming: (params: object) => ['queue', 'upcoming', params] as const,
  },
  schedule: {
    all: ['schedule'] as const,
    events: (params: object) => ['schedule', 'events', params] as const,
  },
  media: {
    all: ['media'] as const,
    list: (params: object) => ['media', 'list', params] as const,
    detail: (id: string) => ['media', 'detail', id] as const,
  },
  stock: {
    all: ['stock'] as const,
    search: (provider: string, query: string) =>
      ['stock', 'search', provider, query] as const,
  },
  presets: {
    all: ['presets'] as const,
    list: (workspaceId: string) => ['presets', 'list', workspaceId] as const,
  },
  /**
   * Everything read from YouTube, keyed by channel. One prefix so an upload
   * or an edit can refresh the whole section at once.
   */
  youtube: {
    all: ['youtube'] as const,
    config: ['youtube', 'config'] as const,
    overview: (accountId: string) =>
      ['youtube', 'overview', accountId] as const,
    videos: (accountId: string, params: object) =>
      ['youtube', 'videos', accountId, params] as const,
    queue: (accountId: string) => ['youtube', 'queue', accountId] as const,
    video: (accountId: string, videoId: string) =>
      ['youtube', 'video', accountId, videoId] as const,
    playlists: (accountId: string) =>
      ['youtube', 'playlists', accountId] as const,
    defaults: (accountId: string) =>
      ['youtube', 'defaults', accountId] as const,
    bestTimes: (accountId: string) =>
      ['youtube', 'best-times', accountId] as const,
    categories: (accountId: string) =>
      ['youtube', 'categories', accountId] as const,
    languages: (accountId: string) =>
      ['youtube', 'languages', accountId] as const,
    uploads: (targetIds: string[]) =>
      ['youtube', 'uploads', [...targetIds].sort().join(',')] as const,
  },
  socialAccounts: {
    all: ['socialAccounts'] as const,
    list: (workspaceId: string) =>
      ['socialAccounts', 'list', workspaceId] as const,
    /**
     * Nested on purpose, unlike `telegramPairing`: reconnecting an account
     * invalidates `socialAccounts.all`, and the creator info read with the old
     * token should go with it.
     */
    tiktokCreatorInfo: (accountId: string) =>
      ['socialAccounts', 'tiktokCreatorInfo', accountId] as const,
  },
  /**
   * Deliberately NOT nested under `socialAccounts`. Completing a pairing
   * invalidates the account list, and invalidation matches by prefix — a key
   * starting with 'socialAccounts' would invalidate the poll itself and
   * refetch forever.
   */
  telegramPairing: {
    detail: (pairingId: string) => ['telegramPairing', pairingId] as const,
  },
  distributions: {
    all: ['distributions'] as const,
    list: (params: object) => ['distributions', 'list', params] as const,
    detail: (id: string) => ['distributions', 'detail', id] as const,
  },
  jobs: {
    all: ['jobs'] as const,
    stats: () => ['jobs', 'stats'] as const,
    dead: () => ['jobs', 'dead'] as const,
  },
  aiUsage: {
    all: ['aiUsage'] as const,
    range: (from: number, to: number) => ['aiUsage', from, to] as const,
  },
  mediaKit: {
    all: ['mediaKit'] as const,
    editor: (workspaceId: string) =>
      ['mediaKit', 'editor', workspaceId] as const,
    preview: (workspaceId: string) =>
      ['mediaKit', 'preview', workspaceId] as const,
    inquiries: (workspaceId: string, params: object) =>
      ['mediaKit', 'inquiries', workspaceId, params] as const,
  },
  /**
   * The page a sponsor opens. Deliberately NOT under `mediaKit`: it is read
   * with no token and no workspace, and sharing a prefix would let a save in
   * the editor refetch — or an editor fetch populate — the anonymous page.
   */
  publicMediaKit: {
    detail: (slug: string) => ['publicMediaKit', slug] as const,
  },
  /**
   * Everything is keyed by workspace: the same screen, range and filters in
   * another workspace is a different answer.
   */
  analytics: {
    all: ['analytics'] as const,
    status: (workspaceId: string) =>
      ['analytics', 'status', workspaceId] as const,
    overview: (workspaceId: string, params: object) =>
      ['analytics', 'overview', workspaceId, params] as const,
    posts: (workspaceId: string, params: object) =>
      ['analytics', 'posts', workspaceId, params] as const,
    bestTimes: (workspaceId: string, accountId: string | null) =>
      ['analytics', 'bestTimes', workspaceId, accountId] as const,
    summary: (workspaceId: string) =>
      ['analytics', 'summary', workspaceId] as const,
    report: (workspaceId: string, params: object) =>
      ['analytics', 'report', workspaceId, params] as const,
  },
  activityLog: {
    all: ['activityLog'] as const,
    list: (params: object) => ['activityLog', 'list', params] as const,
  },
};
